import React, {useEffect, useState} from 'react'
import {attendeeApiClient} from 'attendee/apiClient'
import {EventModel} from 'event/models/Event'
import {useParamEventSlug} from 'event/EventRoutes'
import {toast} from 'react-toastify'
import {FullPageLoader} from 'organization/theme/FullPageLoader'

export interface EventPreviewContextProps {
  event: EventModel | null
  loading: boolean
}

export const EventPreviewContext = React.createContext<
  EventPreviewContextProps | undefined
>(undefined)

export default function EventPreviewProvider(props: {
  children: React.ReactElement
}) {
  const apiClient = attendeeApiClient()
  const [event, setEvent] = useState<EventModel | null>(null)
  const [loading, setLoading] = useState<boolean>(true)

  const eventPreviewSlug = useParamEventSlug()

  useEffect(() => {
    const get = async () => {
      try {
        const response = await apiClient.get(
          `api/event/${eventPreviewSlug}/preview`,
        )
        setEvent(response)
        setLoading(false)
      } catch (error: any) {
        toast.warning(error.response.data.message)
        setLoading(false)
      }
    }

    get()
    // Intentional mount-only fetch; listing apiClient/slug triggers redundant refetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [])

  return (
    <EventPreviewContext.Provider
      value={{
        event,
        loading,
      }}
    >
      {loading && <FullPageLoader />}
      {!loading && props.children}
    </EventPreviewContext.Provider>
  )
}

export function useEventPreview() {
  const context = React.useContext(EventPreviewContext)
  if (context === undefined) {
    throw new Error('useEventPreview must be used within EventPreviewProvider')
  }
  return context
}
